import { Card } from "@/components/ui/card";
import { BwUserInformationIdLine } from "@/components/ui/icons";
import type { PurchaseIntentCheckoutLineItem } from "@/features/shared/checkout-summary";

type PresaleMembershipCardProps = {
  /** The PLAN row of the reservation's cart. */
  membership: PurchaseIntentCheckoutLineItem | undefined;
  /** The one-time services the offer includes, in the link's own order. */
  services: PurchaseIntentCheckoutLineItem[];
  launchDateLabel: string;
};

/**
 * The membership being reserved and what rides with it — the presale twin of
 * `PlanRequiredCard`.
 *
 * The campaign sells one membership at a presale price (the link's Stripe price
 * override, $0/mo today), so there is nothing to pick here — nor anywhere else in the
 * cart: the service-credit card below states its tier read-only.
 *
 * Prices are the platform's own formatted labels, not cents formatted here — the whole
 * checkout reads one server-priced cart, and a second formatter is how the two drift.
 */
export function PresaleMembershipCard({
  membership,
  services,
  launchDateLabel,
}: PresaleMembershipCardProps) {
  return (
    <Card className="p-4 gap-2 border border-border-dark-low rounded-2xl shadow-none">
      <div className="flex gap-2 items-center mb-0">
        <BwUserInformationIdLine size={24} className="text-text-primary" />
        <span className="text-sm leading-5 font-normal grow">
          Membership plan
        </span>
      </div>

      {membership && (
        <div className="border-t pt-2 border-border-dark-low grow text-sm leading-5">
          <p className="font-medium">Founding {membership.product?.name}</p>
          <p className="text-text-dark-high">
            {membership.price?.formattedPrice}/mo until {launchDateLabel}, when
            service starts in your area.
            {membership.product?.description
              ? ` ${membership.product.description}`
              : ""}
          </p>
        </div>
      )}

      {services.map((service) => (
        <div
          key={service.product?.key ?? service.productId}
          className="border-t pt-2 border-border-dark-low grow text-sm leading-5"
        >
          <p className="font-medium">
            {service.product?.name}
            {service.price?.unitAmount === 0 && (
              <span className="text-text-dark-low font-normal">
                {" "}
                · Included
              </span>
            )}
          </p>
          <p className="text-text-dark-high">{service.product?.description}</p>
        </div>
      ))}
    </Card>
  );
}
